import React from "react";
import { RfxListPage } from "../RfxListPage";
import { getRfxListSurface } from "../surfaces/rfx-list-surface";
import { ApprovalsList } from "../components/Approvals/ApprovalsList";
import { ApprovalsEmptyState } from "../components/Approvals/ApprovalsEmptyState";
import { APPROVALS_TABS } from "../components/Approvals/approvalsTabAssertion";
import type { RfxListTab } from "../types/rfx-list.type";

interface RfxApprovalsPageProps {
	companyId?: string;
	path?: string;
	tab?: string;
}

const DEFAULT_TAB: RfxListTab = "current";

const resolveTab = (tab: string | undefined): RfxListTab =>
	(APPROVALS_TABS as readonly string[]).includes(tab ?? "")
		? (tab as RfxListTab)
		: DEFAULT_TAB;

/**
 * Surface descriptor for this list — tabs, title, hrefs, default tab and data
 * source. Module-level because it is pure data: `useList` is a hook, but it is
 * only *called* during the shell's render, never here.
 */
const surface = getRfxListSurface("approvals");

/**
 * Route mount for the Approvals scope. 006 already emits deep links to
 * `/account/{companyId}/rfx/approvals/current` — real row rendering +
 * click-through to the approver-flow target lands here (TASK-019).
 */
export const RfxApprovalsPage = ({ companyId, tab }: RfxApprovalsPageProps) => {
	const resolvedTab = resolveTab(tab);

	return (
		<RfxListPage
			surface={surface}
			companyId={companyId}
			tab={tab}
			renderList={(rows, meta, view) => (
				<ApprovalsList
					rows={rows}
					meta={meta}
					isPending={resolvedTab === "current"}
					view={view}
				/>
			)}
			renderEmptyState={() => <ApprovalsEmptyState tab={resolvedTab} />}
		/>
	);
};
